import { useTranslation } from 'react-i18next';
import {
  highlightTagColorLabelKey,
  highlightTagDisplayName,
  highlightTagInkCss,
  highlightTagsInCatalogOrder,
  type HighlightTagColorSource,
} from '@/lib/highlightTagColors';
import { fileCategorySwatchClass } from '@/lib/patientFileCategories';
import { cn } from '@/utils/cn';

export function HighlightTagFace({
  tag,
  className,
}: {
  tag: { name: string; color_token: string };
  className?: string;
}) {
  const { t } = useTranslation();
  const colorLabel = t(highlightTagColorLabelKey(tag.color_token));
  const named = tag.name.trim();
  const ink = highlightTagInkCss(tag.color_token);
  return (
    <span
      className={cn('inline-flex min-w-0 items-center gap-s2', className)}
      aria-label={
        named ? undefined : t('settings.highlight_tags.unnamed_color', { color: colorLabel })
      }
    >
      <span
        className={cn(
          'h-2.5 w-2.5 shrink-0 rounded-full',
          !ink && fileCategorySwatchClass(tag.color_token),
        )}
        style={ink ? { background: ink } : undefined}
        aria-hidden
      />
      {named ? (
        <span className="truncate font-semibold">{named}</span>
      ) : (
        <span className="truncate">
          <span className="italic">{t('settings.highlight_tags.unnamed')}</span>
          <span aria-hidden> · </span>
          <span>{colorLabel}</span>
        </span>
      )}
    </span>
  );
}

export function HighlightTagPills({
  tags,
  tagIds,
}: {
  tags: readonly HighlightTagColorSource[];
  tagIds?: readonly string[];
}) {
  const { t } = useTranslation();
  const linked = highlightTagsInCatalogOrder(tags, tagIds);
  if (linked.length === 0) return null;
  return (
    <span className="fp-tag-pills">
      {linked.map((tag) => {
        const colorLabel = t(highlightTagColorLabelKey(tag.color_token));
        const label = highlightTagDisplayName(
          tag,
          t('settings.highlight_tags.unnamed'),
          colorLabel,
        );
        const ink = highlightTagInkCss(tag.color_token);
        return (
          <span key={tag.id} className="fp-tag-pill">
            <i
              className={cn(!ink && fileCategorySwatchClass(tag.color_token))}
              style={ink ? { background: ink } : undefined}
              aria-hidden
            />
            <span className={cn(!tag.name.trim() && 'is-unnamed')}>{label}</span>
          </span>
        );
      })}
    </span>
  );
}
